<template>
  <div class="mall-search">
    <div class="page">
      <MallHeader @back-click="goBack">
        <div slot="right"></div>
      </MallHeader>

      <div class="mall-search__content" :class="{ 'mall-search__content--result': isResult }">
        <MallSearchBar
          ref="searchBar"
          v-model="keyword"
          :placeholder="'상품명, 카테고리명 등 입력'"
          :autofocus="!initialKeyword"
          @search="search"
        />

        <!-- 검색 결과 -->
        <span v-if="isResult" v-show="isLoaded" class="mall-search__count">검색결과 총 {{ results.length }}개</span>

        <!-- 최근 검색어 -->
        <MallRecentKeywords
          v-else-if="recentKeywords.length > 0"
          :keywords="recentKeywords"
          @select="search"
          @remove="removeRecentKeyword"
          @clear="clearRecentKeywords"
        />
      </div>

      <MallProductGrid
        v-if="isResult && isLoaded"
        :products="results"
        @select="openPurchaseSheet"
      />

      <MallPurchaseLayer
        :product="purchaseProduct"
        :profileId="profileId"
        :productMap="catalog.productMap"
        @close="purchaseProduct = null"
      />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />
    </div>
  </div>
</template>

<script>
import mall from '@/utilities/mall.js'
import MallHeader from '@/components/new/molecules/MallHeader.vue'
import MallSearchBar from '@/components/new/molecules/MallSearchBar.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallRecentKeywords from '@/components/new/organisms/MallRecentKeywords.vue'
import MallProductGrid from '@/components/new/organisms/MallProductGrid.vue'
import MallPurchaseLayer from '@/components/new/organisms/MallPurchaseLayer.vue'

// 최근 검색어 저장 (브라우저 localStorage, 최신순 최대 10개)
var RECENT_KEYWORDS_KEY = 'mallRecentKeywords'
var RECENT_KEYWORDS_MAX = 10

export default {
  name: 'MallSearch',
  components: {
    MallHeader,
    MallSearchBar,
    MallToast,
    MallRecentKeywords,
    MallProductGrid,
    MallPurchaseLayer,
  },
  data() {
    var initialKeyword = this.$route.query.keyword || ''
    return {
      profileId: Number(this.$route.query.profileId) || null,
      initialKeyword: initialKeyword,
      keyword: initialKeyword,
      // 마지막으로 검색한 검색어 (비어 있으면 최근 검색어 노출)
      searchedKeyword: '',
      recentKeywords: [],
      purchaseProduct: null,
      toast: null,

      // 카테고리 / 전체 상품 (mall.fetchCatalog)
      // TODO: 상품 검색 API가 없어 전체 상품을 받아 화면에서 검색
      catalog: {
        categories: [],
        products: [],
        productMap: {},
      },
      isLoaded: false,
    }
  },
  computed: {
    isResult() {
      return this.searchedKeyword !== ''
    },
    products() {
      return this.catalog.products.map(product => mall.toProductView(product, this.catalog.categories))
    },
    // 상품명 / 카테고리명에 검색어가 들어간 상품 (대소문자 구분 없음)
    results() {
      var keyword = this.searchedKeyword.toLowerCase()
      return this.products.filter(product => {
        return product.name.toLowerCase().indexOf(keyword) > -1 || product.categoryLabel.toLowerCase().indexOf(keyword) > -1
      })
    },
  },
  watch: {
    keyword(value) {
      // 검색어를 모두 지우면 최근 검색어로 돌아감
      if (value.trim() === '' && this.searchedKeyword) {
        this.searchedKeyword = ''
        this.$router.replace({ path: '/mall/search', query: { profileId: this.profileId } })
      }
    },
  },
  created() {
    this.recentKeywords = this.loadRecentKeywords()
    if (this.initialKeyword) {
      this.search(this.initialKeyword)
    }
  },
  mounted() {
    ChannelIO('shutdown')
    this.fetchCatalog()
  },
  methods: {
    fetchCatalog() {
      var token = mall.getAccessToken()
      if (!token) {
        mall.goLogin(this.$router)
        return
      }
      mall.fetchCatalog(this.$store, this.profileId, token)
        .then(catalog => {
          this.catalog = catalog
          this.isLoaded = true
        })
        .catch(err => {
          if (mall.isAuthError(err)) {
            mall.goLogin(this.$router)
            return
          }
          this.toast = { key: Date.now(), title: '상품을 불러오지 못했어요.', message: mall.errorMessage(err) }
        })
    },
    goBack() {
      mall.goBack(this.$router, this.$route, { path: '/mall/shop', query: { profileId: this.profileId } })
    },
    search(value) {
      var keyword = String(value || '').trim()
      if (!keyword) return
      this.keyword = keyword
      this.searchedKeyword = keyword
      this.saveRecentKeywords([keyword].concat(this.recentKeywords.filter(item => item !== keyword)))
      // 상품 구매 후 뒤로가기로 돌아왔을 때 검색 결과가 유지되도록 주소에 검색어 저장
      if (this.$route.query.keyword !== keyword) {
        this.$router.replace({ path: '/mall/search', query: { profileId: this.profileId, keyword: keyword } })
      }
      // 검색 결과가 가려지지 않도록 키보드 내리기
      if (this.$refs.searchBar) {
        this.$refs.searchBar.blur()
      }
      window.scrollTo(0, 0)
    },
    removeRecentKeyword(keyword) {
      this.saveRecentKeywords(this.recentKeywords.filter(item => item !== keyword))
    },
    clearRecentKeywords() {
      this.saveRecentKeywords([])
    },
    loadRecentKeywords() {
      try {
        var saved = JSON.parse(window.localStorage.getItem(RECENT_KEYWORDS_KEY) || '[]')
        return Array.isArray(saved) ? saved.filter(item => typeof item === 'string') : []
      } catch (e) {
        return []
      }
    },
    saveRecentKeywords(keywords) {
      this.recentKeywords = keywords.slice(0, RECENT_KEYWORDS_MAX)
      try {
        window.localStorage.setItem(RECENT_KEYWORDS_KEY, JSON.stringify(this.recentKeywords))
      } catch (e) {
        // 저장할 수 없는 환경(사생활 보호 모드 등)에서는 화면에서만 유지
      }
    },
    openPurchaseSheet(productId) {
      this.purchaseProduct = this.products.find(product => product.id === productId) || null
    },
  },
}
</script>

<style scoped>
  .mall-search {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
  }
  .page {
    width: 100%;
    min-height: 100vh;
    background: #FFF;
  }
  .mall-search__content {
    display: flex;
    padding: 4px 16px 32px 16px;
    flex-direction: column;
    gap: 12px;
  }
  /* 검색 결과: 검색창 ↔ 결과 수 16px, 아래 상품 목록은 MallProductGrid 위 여백(16px) 사용 */
  .mall-search__content--result {
    padding-bottom: 0;
    gap: 16px;
  }
  .mall-search__count {
    align-self: flex-start;
    color: #4E5968;
    font-family: Pretendard;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-normal);
    text-decoration: underline;
    white-space: nowrap;
  }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallSearch.vue